import { FC, useEffect, useImperativeHandle, useState, forwardRef } from 'react'
import Image from 'next/image'
import { useRouter } from 'next/router'
import PdStandaloneVideo from '../pd-standalone-video/pd-standalone-video'
import React from 'react'
import { useTranslations } from 'next-intl'
import { EventReportManager } from '../../event-report/event-report-manager'

type WatchVideoRef = { playStatus: () => boolean; startPlay: (videoUrlRefProps: string) => void }

interface WatchVideoProps {
  playingStatusChange?: (status: boolean) => void
  videoUrl?: string
  className: string
  textClassName?: string
  type?: 'primary' | 'normal'
}

const WatchVideo = forwardRef<WatchVideoRef, WatchVideoProps>(
  ({ playingStatusChange, videoUrl: videoUrlProps, className, textClassName, type = 'normal' }, ref) => {
    const [videoUrl, setVideoUrl] = useState<string>(videoUrlProps || '')
    const colorClass = type === 'normal' ? 'bg-gray-100' : 'bg-pdblue'
    const textColorClass = type === 'normal' ? 'text-black/90' : 'text-white/90'
    const rootNodeClass = [
      'w-fit h-fit px-4 py-2 rounded-[19px] justify-center items-center gap-1 inline-flex cursor-pointer',
      colorClass,
    ].join(' ')
    const [playing, setPlaying] = useState<boolean>(false)
    const t = useTranslations()
    const router = useRouter()

    useImperativeHandle(ref, () => ({
      playStatus: () => {
        return playing
      },
      startPlay: (videoUrlRefProps) => {
        setVideoUrl(videoUrlRefProps)
        startPlay(videoUrlRefProps)
      },
    }))

    useEffect(() => {
      if (videoUrlProps) {
        setVideoUrl(videoUrlProps)
      }
    }, [videoUrlProps])

    useEffect(() => {
      !!playingStatusChange && playingStatusChange(playing)
    }, [playing])

    function startPlay(url?: string) {
      setPlaying(true)

      // report play video event
      const videoId = router.asPath
      const videoTitle = videoId

      EventReportManager.getInstance().fireEventImmediately(
        {
          data: {
            event: 'custom_video_play',
            video_id: videoId,
            video_title: videoTitle,
            video_url: url || videoUrl,
            page_url: window.location.href,
          },
        },
        true
      )
    }

    function stopPlay() {
      setPlaying(false)
    }

    return (
      <>
        <div className={[rootNodeClass, className].join(' ')} onClick={() => startPlay(videoUrl)}>
          <div className={['text-sm font-normal leading-snug', textColorClass, textClassName].join(' ')}>
            {t('WatchVideo')}
          </div>

          <Image
            className="w-14px h-14px"
            src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTExLjY4IDcuMzg2OTlDMTIuMTA2NyA3LjY1OTQ0IDEyLjEwNjcgOC4zNDA1NiAxMS42OCA4LjYxMzAxTDQuOTYgMTIuOTA0MUM0LjUzMzMzIDEzLjE3NjYgNCAxMi44MzYgNCAxMi4yOTExTDQgMy43MDg5QzQgMy4xNjQgNC41MzMzMyAyLjgyMzQ0IDQuOTYgMy4wOTU4OUwxMS42OCA3LjM4Njk5WiIgZmlsbD0iYmxhY2siIGZpbGwtb3BhY2l0eT0iMC45Ii8+Cjwvc3ZnPgo="
            alt="play icon"
            width={'14px'}
            height={'14px'}
            style={{ filter: type === 'normal' ? '' : 'invert(100%)' }}
          />
        </div>
        <PdStandaloneVideo source={videoUrl} show={playing} onClosed={() => stopPlay()} />
      </>
    )
  }
)

export default WatchVideo
WatchVideo.displayName = 'PdWatchVideo'
